import {
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP, ROUTES } from 'config';
import {
  surveyPendingSubmissionsFiltersSchema,
  useSurvey,
  useSurveyPendingSubmissions,
} from 'store/surveys';
import { useRouteParams } from 'utils/useIdParam';
import { useSearchParams } from 'utils/navigation';
import {
  FilterButton,
  Filters,
  OpenSearchFilter,
  OrgStructureFilter,
} from 'components/filters';
import { SubmissionsTrainingTabsSwitcher } from 'containers/surveys';
import { CompanyScopeInfoMessage } from 'containers/employees';

export const SurveyPendingSubmissionsTraining = () => {
  const { t } = useTranslation();
  const { id: surveyId } = useRouteParams(ROUTES.surveys.survey.details);

  const { searchParams: filters, navigate } = useSearchParams(
    surveyPendingSubmissionsFiltersSchema,
  );

  const { data: survey } = useSurvey(surveyId);
  const { data: submissions, isLoading: isSubmissionsLoading } =
    useSurveyPendingSubmissions(surveyId, filters);

  if (!survey) return null;

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <SubmissionsTrainingTabsSwitcher surveyId={surveyId} />

          <CompanyScopeInfoMessage />

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={submissions?.total} />

            <Filters
              value={filters}
              initialValues={surveyPendingSubmissionsFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              keysToExtractFromTotalCalculation={['search']}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('home.organisationStructure_title')}
                    value={value.orgStructure}
                    initialValue={initialValues.orgStructure}
                    onChange={(v) => setFilter('orgStructure', v)}
                    contentMaxWidth={420}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <OrgStructureFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={submissions?.items ?? []}
        isLoading={isSubmissionsLoading}
        columns={[
          {
            id: 'name',
            header: t('common.full_name'),
            cell: ({ row: { original: submission } }) =>
              submission.employee ? (
                <Link
                  label={submission.employee.name}
                  to={ROUTES.surveys[survey.type].submissions.details
                    .replace(':id', surveyId)
                    .replace(':submissionId', submission.id)}
                />
              ) : (
                <Typography color="textSecondary">
                  {t('trustBox.anonymousEmployeeName')}
                </Typography>
              ),
            meta: { sort: true },
          },
          {
            id: 'company',
            header: t('employees.company'),
            cell: ({ row: { original: submission } }) => {
              return submission.company?.name ?? '-';
            },
          },
          {
            id: 'department',
            header: t('employees.department'),
            cell: ({ row: { original: submission } }) => {
              return submission.department?.name ?? '-';
            },
            meta: { sort: true },
          },
        ]}
        pagination={
          submissions && {
            count: submissions.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
      />
    </TableWrapper>
  );
};
